import type { FC } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CrudLink, CustomCellDate } from "@/shared/components";
import { touchSummary } from "@/shared/components/AttributionTouch";
import { PagePaths } from "@/admin/shared/constants";
import { audienceLabel } from "@/admin/entities/assistant";
import type { AssistantConversationShowData } from "@/admin/entities/assistant";
import { topicLabel } from "../model/topicLabel";

interface AssistantConversationDetailsProps {
  /** CrudShow разворачивает данные showQuery в пропсы компонента. */
  assistantConversation?: AssistantConversationShowData | null;
  /** id диалога CrudShow передаёт отдельным пропом; здесь не нужен. */
  id?: string;
}

const roleLabel: Record<string, string> = {
  USER: "Зритель",
  ASSISTANT: "Помощник",
};

const kindChipConfig: Record<
  string,
  { label: string; color: ChipProps["color"] }
> = {
  OFF_TOPIC: { label: "Не по теме", color: "default" },
  BLOCKED: { label: "Заблокировано", color: "warning" },
  ERROR: { label: "Ошибка", color: "error" },
};

const InfoItem: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {children}
    </Typography>
  </Box>
);

export const AssistantConversationDetails: FC<
  AssistantConversationDetailsProps
> = ({ assistantConversation }) => {
  if (!assistantConversation) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Диалог не найден
        </Typography>
      </Paper>
    );
  }

  const conversation = assistantConversation;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            {conversation.title || "Без заголовка"}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <InfoItem label="Арена">
              {conversation.platform ? (
                <CrudLink
                  path={PagePaths.platforms}
                  tab="details"
                  id={conversation.platform.id}
                >
                  {conversation.platform.title}
                </CrudLink>
              ) : (
                "-"
              )}
            </InfoItem>
            <InfoItem label="Аудитория">
              {audienceLabel(conversation.audience)}
            </InfoItem>
            <InfoItem label="Организация">
              {conversation.organization ? (
                <CrudLink
                  path={PagePaths.organizations}
                  tab="details"
                  id={conversation.organization.id}
                >
                  {conversation.organization.title}
                </CrudLink>
              ) : (
                "-"
              )}
            </InfoItem>
            <InfoItem label="Пользователь">
              {conversation.user ? (
                <CrudLink
                  path={PagePaths.users}
                  tab="details"
                  id={conversation.user.id}
                >
                  {conversation.user.displayName ||
                    conversation.user.email ||
                    conversation.user.id}
                </CrudLink>
              ) : (
                "-"
              )}
            </InfoItem>
            <InfoItem label="Тема">
              {conversation.topic
                ? (topicLabel[conversation.topic] ?? conversation.topic)
                : "-"}
            </InfoItem>
            <InfoItem label="Создан">
              <CustomCellDate date={conversation.createdAt as string} />
            </InfoItem>
            <InfoItem label="Пришёл">{touchSummary(conversation.utm)}</InfoItem>
            <InfoItem label="Последнее сообщение">
              <CustomCellDate date={conversation.lastMessageAt as string} />
            </InfoItem>
            <InfoItem label="Обращение в поддержку">
              {conversation.hasRequest ? "Да" : "Нет"}
            </InfoItem>
            <InfoItem label="Реакции">
              👍 {conversation.likes} / 👎 {conversation.dislikes}
            </InfoItem>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Метаданные хода
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <InfoItem label="Модель">{conversation.model}</InfoItem>
            <InfoItem label="Стоимость">
              ${conversation.costUsd.toFixed(3)}
            </InfoItem>
            <InfoItem label="Токены (вход / выход)">
              {conversation.inputTokens} / {conversation.outputTokens}
            </InfoItem>
            <InfoItem label="Токены кеша (запись / чтение)">
              {conversation.cacheWriteTokens} / {conversation.cacheReadTokens}
            </InfoItem>
            <InfoItem label="Origin">{conversation.origin}</InfoItem>
            <InfoItem label="Origin error code">
              {conversation.originErrorCode ?? "-"}
            </InfoItem>
            <InfoItem label="Не знал ответ">
              {conversation.knowledgeGap ? "Да" : "Нет"}
            </InfoItem>
          </Box>
          {conversation.knowledgeGapQuestions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Вопросы без ответа в базе знаний
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {conversation.knowledgeGapQuestions.map((question, index) => (
                  <Typography variant="body2" key={index}>
                    {question}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Сообщения
          </Typography>
          <Stack spacing={2} divider={<Divider />}>
            {conversation.messages.map((message) => {
              const kindConfig = kindChipConfig[message.kind];
              return (
                <Box key={message.id}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">
                      {message.role === "USER" &&
                      conversation.audience === "PARTNER"
                        ? "Сотрудник"
                        : (roleLabel[message.role] ?? message.role)}
                    </Typography>
                    {kindConfig && (
                      <Chip
                        label={kindConfig.label}
                        color={kindConfig.color}
                        size="small"
                      />
                    )}
                    {message.rating && (
                      <Typography variant="body2">
                        {message.rating === "UP" ? "👍" : "👎"}
                      </Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      <CustomCellDate date={message.createdAt as string} />
                    </Typography>
                  </Stack>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {message.text}
                  </Typography>
                  {message.page && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      Раздел:{" "}
                      {(message.page as { section?: string }).section ?? "-"} (
                      {(message.page as { path?: string }).path ?? ""})
                    </Typography>
                  )}
                  {message.attachments.length > 0 && (
                    <Stack direction="row" gap={1} sx={{ mt: 0.5 }}>
                      {message.attachments.map((a) => (
                        <Box
                          key={a.id}
                          component="img"
                          src={a.url}
                          alt=""
                          sx={{
                            width: 64,
                            height: 64,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  {message.plan && (
                    <Typography variant="body2" component="div">
                      План:{" "}
                      <CrudLink
                        path={PagePaths.assistantPlans}
                        tab="details"
                        id={message.plan.id}
                      >
                        {message.plan.title}
                      </CrudLink>{" "}
                      ({message.plan.status})
                    </Typography>
                  )}
                  {message.error && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {message.error}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    ${message.costUsd.toFixed(3)} · {message.durationMs} мс
                    {message.pageUrl ? ` · ${message.pageUrl}` : ""}
                  </Typography>
                  <Accordion sx={{ mt: 1 }} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2">
                        Содержимое ответа (включая вызовы ручек)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          fontSize: 12,
                          overflowX: "auto",
                          fontFamily: "monospace",
                        }}
                      >
                        {JSON.stringify(message.content, null, 2)}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
